import React from "react";
import { Alert, Box, Grid, Stack, useTheme } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";
import { SSButton } from "src/components/base/components/SSButton";

import {
  handleAddOnCountLogic,
  isAddOnCounterDisabled,
  handleInventoryGroupSeatCount,
  isAddOnRequiredInCart,
} from "./utils.homepageFlow";
import { pluralize } from "src/utils/utils";
import { useEventCardCtx } from "./EventCard";
import { useHomePageFlow } from "./useHomePageFlow";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useGlobalStore } from "src/components/reusable/hooks/useGlobalStore";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TAddOn } from "src/shared/types/globalStotre.types";

function AddOnCounter(props: { addOn: TAddOn; isDisabled?: boolean }) {
  const theme = useSSTheme();
  const { events } = useGlobalStore();
  const teamConfig = useTeamConfig();
  const { onChangeAddOnSeatCount } = useHomePageFlow();
  const { seatCount, maxSeatCount, minSeatCount, increment, seatsClaimed } = props.addOn; // prettier-ignore
  const [claimableMaxCount, setClaimableMaxCount] = React.useState(maxSeatCount - seatsClaimed); // prettier-ignore
  const [inventoryGroupSeatCount, setInventoryGroupSeatCount] = React.useState(0); // prettier-ignore

  // a required add on is locked to `minSeatCount` while another add on for the
  // same event is in the cart, instead of the usual "drop straight to zero from
  // the minimum" behaviour.
  const event = events.find((evt) => evt.eventCode === props.addOn.gameEventCode); // prettier-ignore
  const isSeatCountLocked = isAddOnRequiredInCart(
    props.addOn,
    event,
    teamConfig
  );
  const seatCountFloor = isSeatCountLocked ? minSeatCount : 0;

  const handleChange = (isIncrement: boolean) => {
    let newSeatCount = seatCount;
    if (isIncrement) {
      if (seatCount < minSeatCount) newSeatCount = minSeatCount;
      else newSeatCount = Math.min(claimableMaxCount, seatCount + increment);
    } else {
      if (!isSeatCountLocked && seatCount == minSeatCount) newSeatCount = 0;
      else newSeatCount = Math.max(seatCountFloor, newSeatCount - increment);
    }
    onChangeAddOnSeatCount({ ...props.addOn, seatCount: newSeatCount });
  };

  const btnDefaultStyle = {
    minWidth: "32px",
    width: "32px",
    minHeight: "36px",
    borderRadius: "0px",
    fontSize: "24px",
    lineHeight: 0.8,
  };

  // handles manual variant seat count if there is inventory group
  // prettier-ignore
  React.useEffect(() => {
    handleInventoryGroupSeatCount(
      props.addOn,
      events,
      setInventoryGroupSeatCount,
      setClaimableMaxCount
    )
  }, [events]);

  React.useEffect(() => {
    setClaimableMaxCount(maxSeatCount - seatsClaimed);
  }, [maxSeatCount, seatsClaimed]);

  return (
    <Stack
      direction={"row"}
      sx={{
        opacity: props?.isDisabled ? 0.5 : 1,
        pointerEvents: props?.isDisabled ? "none" : "auto",
        cursor: props?.isDisabled ? "not-allowed" : "auto",
        border: `1px solid ${theme.colors.teamSecondary}`,
        borderRadius: "6px",
        overflow: "hidden",
      }}
    >
      <SSButton
        variant="contained"
        disabled={seatCount <= seatCountFloor}
        colorScheme={theme.colors.teamSecondary}
        sx={btnDefaultStyle}
        onClick={(e: any) => handleChange(false)}
      >
        -
      </SSButton>
      <Stack
        minWidth={"42px"}
        bgcolor={theme.colors.common.white}
        justifyContent={"center"}
        alignItems={"center"}
        flex={1}
      >
        <SSText variant="body" fontWeight={700}>
          {seatCount}
        </SSText>
      </Stack>
      <SSButton
        variant="contained"
        disabled={(inventoryGroupSeatCount || seatCount) >= claimableMaxCount}
        colorScheme={theme.colors.teamSecondary}
        sx={btnDefaultStyle}
        onClick={(e: any) => handleChange(true)}
      >
        +
      </SSButton>
    </Stack>
  );
}

function AddOnAddToCartButton(props: {
  addOn: TAddOn;
  isDarkBg?: boolean;
  isDisabled?: boolean;
}) {
  const theme = useSSTheme();
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const { onChangeAddOnSeatCount } = useHomePageFlow();
  const { seatCount } = props.addOn;
  const isAddedToCart = seatCount > 0;

  const onClick = () => {
    if (isAddedToCart) {
      onChangeAddOnSeatCount({ ...props.addOn, seatCount: 0 });
    } else {
      onChangeAddOnSeatCount({
        ...props.addOn,
        seatCount: Math.max(
          props.addOn.maxSeatCount - props.addOn.seatsClaimed,
          0
        ),
      });
    }
  };

  return (
    <SSButton
      variant={"contained"}
      colorScheme={
        !isAddedToCart
          ? HOME_PAGE_SETTINGS?.ADD_ON?.ADD_BUTTON_COLOR ||
            theme.colors.teamSecondary
          : props.isDarkBg
            ? theme.colors.gray[400]
            : theme.colors.common.black
      }
      size="medium"
      fullWidth
      onClick={() => onClick()}
      sx={{
        minWidth: "fit-content",
        minHeight: "36px",
        // borderRadius: "6px",
      }}
      disabled={props.isDisabled}
    >
      {isAddedToCart ? "Remove" : "Add"}
    </SSButton>
  );
}

export function AddOnItem(props: { addOn: TAddOn; isDarkBg?: boolean; isDisabled?: boolean }) {
  const muiTheme = useTheme();
  const { events } = useGlobalStore();
  const eventCardCtx = useEventCardCtx();
  const { HOME_PAGE_SETTINGS } = useTeamConfig();
  const [isCounterDisabled, setIsCounterDisabled] = React.useState(false);
  const [maxCount, setMaxCount] = React.useState(props.addOn.maxSeatCount - props.addOn.seatsClaimed); // prettier-ignore

  const isFixedVariant =
    props.addOn.seatCountVariation == "FIXED" ||
    props.addOn.seatCountVariation == "SAME AS PASS" ||
    props.addOn.seatCountVariation == "SAME AS PASS AND GUEST" ||
    props.addOn.seatCountVariation == "SAME AS SEATS ON ACCOUNT";

  const isManualVariant =
    props.addOn.seatCountVariation == "MANUAL" ||
    props.addOn.seatCountVariation == "UP TO PASS" ||
    props.addOn.seatCountVariation == "UP TO PASS AND GUEST" ||
    props.addOn.seatCountVariation == "UP TO SEATS ON ACCOUNT";

  // price for both fixed and manual
  const addOnPrice = HOME_PAGE_SETTINGS?.ADD_ON?.ADD_SERVICE_FEE_TO_PRICE
    ? props.addOn.price + props.addOn.serviceFee
    : props.addOn.price;

  // seat count for fixed variant
  const fixedSeatCount = Math.max(
    props.addOn.maxSeatCount - props.addOn.seatsClaimed,
    0
  );

  React.useEffect(() => {
    setIsCounterDisabled(
      isAddOnCounterDisabled(props.addOn, [
        ...eventCardCtx.addOns,
        ...eventCardCtx.singleGames,
      ])
    );
  }, [eventCardCtx.addOns]);

  // handles max count logic for fixed/manual variants
  React.useEffect(() => {
    handleAddOnCountLogic(
      props.addOn,
      events,
      setMaxCount,
      setIsCounterDisabled
    );
  }, [events]);

  return (
    <Stack>
      {/* <SSText color={"white"}>{props?.addOn?.productId}</SSText> */}
      <Grid container alignItems={"center"} spacing={1}>
        <Grid item xs={4} sm={5} md={6}>
          <Stack spacing={0.2}>
            <SSText
              variant="body"
              fontWeight={700}
              lineHeight={1.1}
              fontSize={"0.95rem"}
              color={eventCardCtx.colors.textColor}
            >
              {props.addOn.productTitle}
            </SSText>

            {props?.addOn?.productDescription ? (
              <SSText
                variant="body"
                fontSize={"0.95rem"}
                color={eventCardCtx.colors.textColor}
                sx={{ opacity: 0.7, marginTop: "4px !important" }}
                lineHeight={1.1}
              >
                {props.addOn.productDescription}
              </SSText>
            ) : null}
          </Stack>
        </Grid>
        <Grid item xs={3} sm={4} md={4}>
          <SSText
            variant="body"
            fontSize={"0.95rem"}
            color={eventCardCtx.colors.textColor}
            lineHeight={1.1}
          >
            {isManualVariant ? <>$ {addOnPrice.toFixed(2)}</> : null}

            {isFixedVariant && fixedSeatCount > 0 ? (
              <>
                $ {(addOnPrice * fixedSeatCount).toFixed(2)} for
                <br />
                {fixedSeatCount} {pluralize("Seat", fixedSeatCount)}
              </>
            ) : null}

            {isFixedVariant && fixedSeatCount <= 0 ? (
              <>$ {addOnPrice.toFixed(2)}</>
            ) : null}
          </SSText>
        </Grid>

        <Grid item xs={5} sm={3} md={2}>
          {!props.addOn?.isSoldOut &&
            !(props.addOn.seatsClaimed >= props.addOn.maxSeatCount) ? (
            <React.Fragment>
              {isManualVariant ? (
                <>
                  <AddOnCounter
                    addOn={props.addOn}
                    isDisabled={isCounterDisabled || props?.isDisabled}
                  />

                  {!HOME_PAGE_SETTINGS?.ADD_ON?.HIDE_MAX_COUNT_LABEL ? (
                    <Box textAlign={"center"} mt={0.5}>
                      <SSText
                        variant="caption"
                        color={eventCardCtx.colors.textColor}
                        fontSize={"0.85rem"}
                      >
                        Max Count: {maxCount}
                      </SSText>
                    </Box>
                  ) : null}
                </>
              ) : null}
              {isFixedVariant ? (
                <AddOnAddToCartButton
                  addOn={props.addOn}
                  isDarkBg={props.isDarkBg}
                  isDisabled={
                    isCounterDisabled || props.addOn.minSeatCount == 0 || props?.isDisabled
                  }
                />
              ) : null}
            </React.Fragment>
          ) : null}
        </Grid>
      </Grid>

      {props.addOn?.isSoldOut ? (
        <SSText
          variant="body"
          fontSize={"0.95rem"}
          color={muiTheme.palette.warning.main}
          mt={0.5}
        >
          Sold Out
        </SSText>
      ) : null}

      {props.addOn.seatsClaimed >= props.addOn.maxSeatCount ? (
        <SSText
          variant="body"
          fontSize={"0.95rem"}
          color={muiTheme.palette.warning.main}
          mt={0.5}
        >
          {HOME_PAGE_SETTINGS?.ADD_ON?.CUSTOM_MAX_SEAT_CLAIMED_MESSAGE ||
            "You have claimed your max limit"}
        </SSText>
      ) : null}

      {props?.isDisabled ? (
        <Alert
          severity="info"
          sx={{
            py: 0.5,
            mt: 1,
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          <SSText variant="body" fontSize={"0.9rem"} lineHeight={1.5}>
            {HOME_PAGE_SETTINGS?.ADD_ON?.OPT_IN_REQUIRED_MESSAGE ||
              "Please confirm your pass booking before you can purchase additional guest seats"}
          </SSText>
        </Alert>
      ) : null}

    </Stack>
  );
}
